import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { DbGate } from './DbGate';
import { Layout } from './Layout';
import { UpdatePrompt } from './UpdatePrompt';
import { useAuth } from '../auth/session';
import { SignInPage } from '../auth/SignInPage';
import { AccountGate } from '../account/AccountGate';
import { SyncProvider } from '../sync/SyncProvider';
import { OnboardingGate } from '../onboarding/OnboardingGate';
import { requestedMode } from '../db/session';
import { TodayPage } from '../features/today/TodayPage';
import { SessionsPage } from '../features/sessions/SessionsPage';
import { SessionPage } from '../features/sessions/SessionPage';
import { MedicationFormPage } from '../features/meds/MedicationFormPage';
import { LabsPage } from '../features/labs/LabsPage';
import { LabReportPage } from '../features/labs/LabReportPage';
import { LabReportFormPage } from '../features/labs/LabReportForm';
import { MorePage } from '../features/more/MorePage';
import { AccountPage } from '../features/more/AccountPage';
import { ProfilePage } from '../features/more/ProfilePage';
import { SettingsPage } from '../features/more/SettingsPage';
import { AboutPage } from '../features/more/AboutPage';
import { NotesPage } from '../features/notes/NotesPage';

// Screens with charts, PDF or backup code, or that are rarely opened, load on first visit. The
// service worker precaches every chunk, so they still open offline.
const HealthPage = lazy(() => import('../features/health/HealthPage').then((module) => ({ default: module.HealthPage })));
const LabTestPage = lazy(() => import('../features/labs/LabTestPage').then((module) => ({ default: module.LabTestPage })));
const LabCatalogPage = lazy(() => import('../features/labs/LabCatalogPage').then((module) => ({ default: module.LabCatalogPage })));
const DiagnosticsPage = lazy(() => import('../features/more/DiagnosticsPage').then((module) => ({ default: module.DiagnosticsPage })));
const RemindersPage = lazy(() => import('../features/reminders/RemindersPage').then((module) => ({ default: module.RemindersPage })));
const ExpensesPage = lazy(() => import('../features/expenses/ExpensesPage').then((module) => ({ default: module.ExpensesPage })));
const NutritionPage = lazy(() => import('../features/nutrition/NutritionPage').then((module) => ({ default: module.NutritionPage })));
const DietPlanPage = lazy(() => import('../features/nutrition/DietPlanPage').then((module) => ({ default: module.DietPlanPage })));
const DoctorReportPage = lazy(() => import('../features/report/DoctorReportPage').then((module) => ({ default: module.DoctorReportPage })));
const ExportImportPage = lazy(() => import('../features/backup/ExportImportPage').then((module) => ({ default: module.ExportImportPage })));

// Local queries are cheap and invalidated on write, so there is no need for time-based refetching.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: Infinity, refetchOnWindowFocus: false, retry: false } }
});

function AppRoutes() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<TodayPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="sessions/new" element={<SessionPage />} />
        <Route path="sessions/:id" element={<SessionPage />} />
        <Route path="health" element={<HealthPage />} />
        <Route path="health/meds/new" element={<MedicationFormPage />} />
        <Route path="health/meds/:id" element={<MedicationFormPage />} />
        <Route path="labs" element={<LabsPage />} />
        <Route path="labs/new" element={<LabReportFormPage />} />
        <Route path="labs/tests" element={<LabCatalogPage />} />
        <Route path="labs/tests/:testId" element={<LabTestPage />} />
        <Route path="labs/:id" element={<LabReportPage />} />
        <Route path="labs/:id/edit" element={<LabReportFormPage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="more/account" element={<AccountPage />} />
        <Route path="more/profile" element={<ProfilePage />} />
        <Route path="more/settings" element={<SettingsPage />} />
        <Route path="more/about" element={<AboutPage />} />
        <Route path="more/reminders" element={<RemindersPage />} />
        <Route path="more/expenses" element={<ExpensesPage />} />
        <Route path="more/notes" element={<NotesPage />} />
        <Route path="more/nutrition" element={<NutritionPage />} />
        <Route path="more/report" element={<DoctorReportPage />} />
        <Route path="more/backup" element={<ExportImportPage />} />
        <Route path="more/nutrition/plans/:id" element={<DietPlanPage />} />
        <Route path="more/diagnostics" element={<DiagnosticsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Demo: no account, in-memory data. Otherwise: sign in → open that account's DB → consent + plan → sync → first-run setup. */
function Gates() {
  const auth = useAuth();

  if (requestedMode() === 'demo') {
    return (
      <DbGate target={{ mode: 'demo' }}>
        <OnboardingGate>
          <AppRoutes />
        </OnboardingGate>
      </DbGate>
    );
  }

  if (auth.status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-clinical-primary" aria-label="Loading" />
      </div>
    );
  }

  if (auth.status === 'signed-out') return <SignInPage />;

  return (
    <DbGate target={{ mode: 'real', userId: auth.user.id }}>
      <AccountGate>
        <SyncProvider>
          <OnboardingGate>
            <AppRoutes />
          </OnboardingGate>
        </SyncProvider>
      </AccountGate>
    </DbGate>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Gates />
        <UpdatePrompt />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
